/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingAudience.module.scss';

import { type AudiencePanel } from './TransferPricingAudience.types';

type Translate = (key: string) => string;

const getPanels = (t: Translate): AudiencePanel[] => [
  {
    name: t('Advisory firms'),
    subtitle: t('Big 4 · mid-tier · boutiques'),
    heading: t('The licence, not the data.'),
    constraint: t(
      'Studies leave the building into client files, local files and APA submissions. Most database terms restrict exactly that, and per-seat pricing punishes a practice as it grows.',
    ),
    answer: t(
      'Redistribution written into standard terms. Priced per jurisdiction and per study, never per named user, so adding clients does not multiply the licence.',
    ),
  },
  {
    name: t('In-house tax'),
    subtitle: t('Multinational groups'),
    heading: t('The audit, and it arrives late.'),
    constraint: t(
      'You sign the study and file it, then defend it three years later against data that has quietly moved underneath you.',
    ),
    answer: t(
      'Sets sealed against the data as it stood that day, with a change feed that flags a restatement before the examiner finds it.',
    ),
  },
  {
    name: t('Tax authorities'),
    subtitle: t('Revenue · customs · FIUs'),
    heading: t('A population, not one file.'),
    constraint: t(
      'Risk-scoring a taxpayer base means resolving entities across borders and mapping the group above each one, mostly from registries you have no pipeline into.',
    ),
    answer: t(
      'Registry-sourced accounts and ownership, with the source filing behind every figure an assessment rests on.',
    ),
  },
];

export const TransferPricingAudience = () => {
  const { t } = useInt();
  const panels = getPanels(t);
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const node = tabRefs.current[active];

    if (!node) {
      return;
    }

    setIndicator({ left: node.offsetLeft, width: node.offsetWidth });
  }, [active]);

  return (
    <Section className={styles.section}>
      <div className={styles.tabs} role="tablist">
        <div
          className={styles.indicator}
          style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
        />

        {panels.map((panel, index) => (
          <button
            key={panel.name}
            type="button"
            role="tab"
            aria-selected={active === index}
            className={cn(styles.tab, active === index && styles.tabOn)}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            onClick={() => setActive(index)}
          >
            <span className={styles.tabName}>{panel.name}</span>
            <span className={styles.tabSubtitle}>{panel.subtitle}</span>
          </button>
        ))}
      </div>

      <div className={styles.stage}>
        {panels.map((panel, index) => (
          <div key={panel.name} className={cn(styles.panel, active === index && styles.panelOn)}>
            <div>
              <div className={styles.panelKicker}>{t('The constraint')}</div>
              <h3 className={styles.panelHeading}>{panel.heading}</h3>
              <p className={styles.panelWhy}>{panel.constraint}</p>
            </div>

            <div className={styles.answer}>
              <div className={cn(styles.panelKicker, styles.answerKicker)}>{t('What we do')}</div>
              <p className={styles.answerText}>{panel.answer}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};
